import { AnimatePresence, motion } from 'motion/react'
import { CSSProperties, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { includes } from '../../utils/array'
import { AstraParagraph } from '../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../Utilities/astraTwMerge'

type ElementWithDataset = HTMLElement | SVGElement
type Placement = (typeof placements)[number]

type TooltipData = {
    tooltip: string
    tooltipPlacement?: string
    tooltipShowArrow?: string
    tooltipNoWrapText?: string
}

type TooltipSearchResult = {
    element: ElementWithDataset
    data: TooltipData
}

let timeoutId: NodeJS.Timeout | null = null
const placements = ['top', 'left', 'bottom', 'right'] as const
const gap = 6

const clipPathMap: Record<Placement, string> = {
    top: 'polygon(100% 100%, 0 10px, 100% 0)',
    bottom: 'polygon(0 0, 100% calc(100% - 10px), 0 100%)',
    left: 'polygon(100% 0, 10px 100%, 0 0)',
    right: 'polygon(0 100%, calc(100% - 10px) 0, 100% 100%)',
}

const enterVariants = {
    top: { opacity: 1, y: 0 },
    left: { opacity: 1, x: 0 },
    bottom: { opacity: 1, y: 0 },
    right: { opacity: 1, x: 0 },
} as const

const initialVariants = {
    top: { opacity: 0, y: 4 },
    left: { opacity: 0, x: 4 },
    bottom: { opacity: 0, y: -4 },
    right: { opacity: 0, x: -4 },
} as const

const exitVariants = {
    top: { opacity: 0, y: 4 },
    left: { opacity: 0, x: 4 },
    bottom: { opacity: 0, y: -4 },
    right: { opacity: 0, x: -4 },
} as const

function findFirstParentWithTooltip(target: ElementWithDataset | null): TooltipSearchResult | null {
    if (!target) return null
    const wrapper = target.closest('[data-tooltip-provider="hero-tooltip-wrapper"]') as ElementWithDataset | null
    if (!wrapper) return null
    if (!(wrapper as HTMLElement).matches(':hover')) return null

    const firstChild = wrapper.firstElementChild as ElementWithDataset | null
    if (!firstChild) return null

    const tooltip = (wrapper as HTMLElement).dataset.tooltip
    if (!tooltip || tooltip === 'self') return null

    const data: TooltipData = {
        tooltip,
        tooltipPlacement: wrapper.dataset.tooltipPlacement,
        tooltipShowArrow: wrapper.dataset.tooltipShowArrow,
        tooltipNoWrapText: wrapper.dataset.tooltipNoWrapText,
    }

    return { element: firstChild, data }
}

export const AstraTooltipProvider = () => {
    const ref = useRef<HTMLDivElement | null>(null)
    const activeElementRef = useRef<ElementWithDataset | null>(null)

    const [text, setText] = useState<string | null>()
    const [position, setPosition] = useState<DOMRect | null>()
    const [placement, setPlacement] = useState<Placement>('top')
    const [showArrow, setShowArrow] = useState(true)
    const [noWrapText, setNoWrapText] = useState(false)

    const [topPosition, setTopPosition] = useState(0)
    const [leftPosition, setLeftPosition] = useState(0)
    const [clipPath, setClipPath] = useState(clipPathMap['top'])
    const [arrowOverride, setArrowOverride] = useState<CSSProperties>({})

    function onMouseOver(e: MouseEvent) {
        const target = e.target
        const isHTMLElement = target instanceof HTMLElement
        const isSVGElement = target instanceof SVGElement
        if (!isHTMLElement && !isSVGElement) return

        const result = findFirstParentWithTooltip(target)
        if (!result) return

        const { element, data } = result
        activeElementRef.current = element

        const box = element.getBoundingClientRect()
        const placementAttr = data.tooltipPlacement
        const showArrowAttr = data.tooltipShowArrow
        const noWrapTextAttr = data.tooltipNoWrapText

        if (timeoutId) clearTimeout(timeoutId)

        timeoutId = setTimeout(() => {
            setText(data.tooltip)
            setPlacement(includes(placements, placementAttr) ? placementAttr : 'top')
            setShowArrow(showArrowAttr !== 'false')
            setNoWrapText(noWrapTextAttr === 'true')
            setTimeout(() => setPosition(box), 1)
        }, 750)

        function removeTooltip(ev: Event) {
            const t = ev.target
            if (!(t instanceof Element)) return
            if (timeoutId) clearTimeout(timeoutId)
            setText(null)
            setPosition(null)
            setNoWrapText(false)
            setShowArrow(true)
            activeElementRef.current = null
        }

        target.addEventListener('click', e => removeTooltip(e), { once: true })
        target.addEventListener('mouseout', e => removeTooltip(e), { once: true })
    }

    function closeOnScroll() {
        setText(null)
        setPosition(null)
        activeElementRef.current = null
    }

    useEffect(() => {
        document.addEventListener('mouseover', onMouseOver)
        return () => document.removeEventListener('mouseover', onMouseOver)
    }, [])

    useEffect(() => {
        document.addEventListener('scroll', closeOnScroll, true)
        return () => document.removeEventListener('scroll', closeOnScroll, true)
    }, [])

    useEffect(() => {
        if (!text) return
        const observer = new MutationObserver(() => {
            if (activeElementRef.current && !document.body.contains(activeElementRef.current)) {
                setText(null)
                setPosition(null)
                activeElementRef.current = null
            }
        })
        observer.observe(document.body, { childList: true, subtree: true })
        return () => observer.disconnect()
    }, [text])

    useEffect(() => {
        if (!text || !position) return
        const tooltipHeight = ref.current?.clientHeight ?? 0
        const tooltipWidth = ref.current?.clientWidth ?? 0
        const scrollY = window.scrollY
        const scrollX = window.scrollX
        const targetTop = position.top + scrollY
        const targetLeft = position.left + scrollX
        const targetWidth = position.width
        const targetHeight = position.height

        let topPos: number
        let leftPos: number
        let idealPlacement = placement

        switch (placement) {
            case 'left':
                topPos = targetTop + targetHeight / 2 - tooltipHeight / 2
                leftPos = targetLeft - tooltipWidth - gap
                break
            case 'right':
                topPos = targetTop + targetHeight / 2 - tooltipHeight / 2
                leftPos = targetLeft + targetWidth + gap
                break
            case 'bottom':
                topPos = targetTop + targetHeight + gap
                leftPos = targetLeft + targetWidth / 2 - tooltipWidth / 2
                break
            case 'top':
                topPos = targetTop - tooltipHeight - gap
                leftPos = targetLeft + targetWidth / 2 - tooltipWidth / 2
                if (topPos < scrollY) {
                    topPos = targetTop + targetHeight + gap
                    idealPlacement = 'bottom'
                }
                break
        }

        const idealLeft = leftPos
        const idealTop = topPos

        // Clamp to visible viewport considering scroll offsets
        const minLeft = scrollX
        const maxLeft = scrollX + window.innerWidth - tooltipWidth
        const minTop = scrollY
        const maxTop = scrollY + window.innerHeight - tooltipHeight

        leftPos = Math.min(Math.max(minLeft, leftPos), maxLeft)
        topPos = Math.min(Math.max(minTop, topPos), maxTop)

        const arrowOverride: CSSProperties = {}
        if (idealPlacement === 'top' || idealPlacement === 'bottom') {
            if (leftPos !== idealLeft) {
                arrowOverride.left = tooltipWidth / 2 + (idealLeft - leftPos)
            }
        } else {
            if (topPos !== idealTop) {
                arrowOverride.top = tooltipHeight / 2 + (idealTop - topPos)
            }
        }

        setClipPath(clipPathMap[idealPlacement])
        setPlacement(idealPlacement)
        setTopPosition(topPos)
        setLeftPosition(leftPos)
        setArrowOverride(arrowOverride)
    }, [text, position, placement])

    return createPortal(
        <AnimatePresence>
            {position && text && (
                <motion.div
                    ref={ref}
                    data-tooltip="self"
                    initial={initialVariants[placement]}
                    animate={enterVariants[placement]}
                    exit={exitVariants[placement]}
                    transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute z-priority block w-fit max-w-sm overflow-visible whitespace-pre-wrap rounded bg-surface-inverted px-2 py-1 text-center text-xs text-white shadow-tooltip"
                    style={{
                        zIndex: 9999,
                        top: topPosition,
                        left: leftPosition,
                        maxWidth: '20rem',
                    }}
                >
                    <AstraParagraph className={`text-inverted ${noWrapText ? 'text-nowrap' : ''}`} variant="body-small">
                        {text}
                    </AstraParagraph>

                    {showArrow && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.12 }}
                            className={astraTwMerge(
                                'pointer-events-none absolute h-2.5 w-2.5 rotate-45 bg-surface-inverted',
                                placement === 'top' ? '-bottom-1 left-1/2 -translate-x-1/2' : '',
                                placement === 'bottom' ? '-top-1 left-1/2 -translate-x-1/2' : '',
                                placement === 'left' ? '-right-1 top-1/2 -translate-y-1/2' : '',
                                placement === 'right' ? '-left-1 top-1/2 -translate-y-1/2' : ''
                            )}
                            style={{ ...arrowOverride, clipPath }}
                        />
                    )}
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    )
}
